import { Fragment, isValidElement } from 'react';
import { isolateComponent, labelStyles, withModifier, } from '@zapier/style-encapsulation';
import { css } from '@emotion/react';
import { BrandButton, Button, Colors, DarkBackgroundButton, Heading, SubscriptionFormWithDisclaimer, Text, Typography, Icon, TagLabel, Grid, } from '../../../../index';
import { getSupportedCTAColor, mapBackgroundColorToV5HeadingColorName, mapBackgroundColorToV5TextColorName, transformBackgroundColor, validateZapierBackgroundAccessibility, } from '../utils';
import { breakpoints } from '../constants';
const SubscriptionFormStyles = {
    ctaContainer: css `
    @media screen and (min-width: ${breakpoints.tablet}px) {
      align-items: stretch;
      flex-direction: column;
    }

    // Button shouldn't take up full width when subscription form is enabled;
    button,
    a {
      width: auto;
    }
  `,
    ctaDisclaimer: css `
    margin: 10px 0;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      margin: 10px 0;
    }
    @media screen and (max-width: ${breakpoints.mobile}px) {
      text-align: left;
    }
  `,
};
const Styles = labelStyles('Hero', {
    root: (backgroundColor = 'neutral100') => css `
    background-color: ${Colors[backgroundColor]};
    padding: 20px;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      padding: 40px;
    }
  `,
    content: (maxWidth) => css `
    display: grid;
    align-items: center;
    grid-template-columns: repeat(1, 1fr);
    gap: 40px;
    max-width: ${maxWidth}px;
    margin: 0 auto;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      grid-template-columns: repeat(2, 1fr);
    }
  `,
    labelContainer: css `
    margin-bottom: 10px;
  `,
    heading: (backgroundColor = 'neutral100') => css `
    ${Typography.pageHeader4}
    color: ${Colors[mapBackgroundColorToV5TextColorName(backgroundColor)]};
    margin-bottom: 10px;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      ${Typography.pageHeader3}
      margin-bottom: 20px;
    }
  `,
    description: (backgroundColor = 'neutral100') => css `
    ${Typography.pageHeader9}
    color: ${Colors[mapBackgroundColorToV5HeadingColorName(backgroundColor)]};
  `,
    textContent: css `
    max-width: 470px;
    text-align: center;
    margin: 0 auto;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      margin: 0;
      text-align: left;
    }
  `,
    mediaContent: css `
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  `,
    ctaContainer: (isSubscriptionForm = false) => css `
    margin-top: 20px;
    text-align: center;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      margin-top: 40px;
      display: flex;
      align-items: center;
      text-align: left;
    }
    ${isSubscriptionForm &&
        withModifier('subscription-form', SubscriptionFormStyles.ctaContainer)}
  `,
    ctaDisclaimer: (isSubscriptionForm = false) => css `
    margin: 10px auto 0 auto;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      margin-top: 0;
      margin-left: 20px;
    }

    ${isSubscriptionForm &&
        withModifier('subscription-form-disclaimer', SubscriptionFormStyles.ctaDisclaimer)}
  `,
});
export const HeroStandard = ({ backgroundColor = 'neutral100', ctaColor = 'primary', isSubscriptionForm = false, subscriptionPlaceholder = 'Email address', heading, description, ctaText, ctaHref, ctaDisclaimer, onCtaClick, onCtaKeyDown, onSubscriptionFormSubmit, children, maxWidth = 1170, ctaTarget, label, labelIcon, labelColor, }) => {
    const mappedBackgroundColor = transformBackgroundColor(backgroundColor);
    validateZapierBackgroundAccessibility(mappedBackgroundColor, isSubscriptionForm, !!ctaDisclaimer);
    const getCtaComponent = () => {
        const sharedProps = {
            children: ctaText,
            href: !isSubscriptionForm ? ctaHref : undefined,
            onClick: onCtaClick,
            onKeyDown: onCtaKeyDown,
            isFullWidth: true,
            type: isSubscriptionForm ? 'submit' : 'button',
            target: ctaTarget,
        };
        const color = getSupportedCTAColor(ctaColor, mappedBackgroundColor);
        return color === 'brand' ? (isolateComponent(BrandButton, Object.assign({}, sharedProps))) : color === 'primary-dark' ? (isolateComponent(DarkBackgroundButton, Object.assign({}, sharedProps))) : (isolateComponent(Button, Object.assign({}, sharedProps, { color: color, size: "small" })));
    };
    const isDisclaimerElement = !!ctaDisclaimer && isValidElement(ctaDisclaimer);
    return (isolateComponent("div", { css: Styles.root(mappedBackgroundColor) },
        isolateComponent("div", { css: Styles.content(maxWidth) },
            isolateComponent("div", { css: Styles.textContent },
                label && (isolateComponent("div", { css: Styles.labelContainer },
                    isolateComponent(TagLabel, { color: labelColor }, labelIcon ? (isolateComponent(Grid, { alignItems: "center", columns: "auto max-content", gap: 5 },
                        isolateComponent(Icon, { name: labelIcon, size: 20 }),
                        label)) : (label)))),
                isolateComponent(Heading, { css: Styles.heading(mappedBackgroundColor) }, heading),
                description && (isolateComponent("div", { css: Styles.description(mappedBackgroundColor) }, description)),
                ctaText && (ctaHref || onCtaClick || isSubscriptionForm) && (isolateComponent("div", { css: Styles.ctaContainer(isSubscriptionForm) }, isSubscriptionForm ? (isolateComponent(SubscriptionFormWithDisclaimer, { placeholderText: subscriptionPlaceholder, onSubmit: onSubscriptionFormSubmit, renderButton: () => getCtaComponent(), disclaimerTextColor: mappedBackgroundColor === 'neutral100'
                        ? 'primary'
                        : 'secondary' })) : (isolateComponent(Fragment, null,
                    isolateComponent("div", null, getCtaComponent()),
                    ctaDisclaimer && (isolateComponent("div", { css: Styles.ctaDisclaimer(isSubscriptionForm) }, isDisclaimerElement ? (ctaDisclaimer) : (isolateComponent(Text, { tag: "p", type: "paragraph3", color: mapBackgroundColorToV5HeadingColorName(mappedBackgroundColor) }, ctaDisclaimer))))))))),
            isolateComponent("div", { css: Styles.mediaContent }, children))));
};
//# sourceMappingURL=HeroStandard.js.map
// @zapier/design-system@5.32.5 (2004a9d50)
